import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { NoopT } from 'declarations/types/common';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useDeleteTicket } from 'hooks/api/useTickets';

import useTicketDetailsPopup from '../../../hooks/useTicketDetailsPopup';

type DeleteTicketParamsT = {
  ticketId: string;
  onClose: NoopT;
};

type UseDeleteTicketPopupT = {
  isDeletingTicket: boolean;
  isTicketDeleted: boolean;
  isTicketDeletedError: boolean;
  deleteTicketErrorMessage: string | undefined;
  deleteTicketValidationErrorMessage: string | undefined;
  showPopupContent: boolean;
  handleCloseDeletePopup: (params: DeleteTicketParamsT) => NoopT;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
};

const useDeleteTicketPopup = (): UseDeleteTicketPopupT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const { handleCloseTicketDetailsPopup } = useTicketDetailsPopup();
  const [isBeingDeleted, setIsBeingDeleted] = useState(false);

  /**
   * Api hooks
   */
  const deleteTicketMutation = useDeleteTicket({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
    },
  });

  /**
   * Variables
   */
  const deleteTicketErrorMessage = useMemo(
    () => getErrorMessage(deleteTicketMutation.error),
    [deleteTicketMutation.error]
  );
  const deleteTicketValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(deleteTicketMutation.error),
    [deleteTicketMutation.error]
  );

  /**
   * Handlers
   */
  const resetDeleteTicketMutationState = useCallback(() => {
    if (deleteTicketMutation.isSuccess || deleteTicketMutation.isError) {
      deleteTicketMutation.reset();
    }
  }, [deleteTicketMutation]);
  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
      resetDeleteTicketMutationState();
      if (isBeingDeleted) {
        handleCloseTicketDetailsPopup();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resetDeleteTicketMutationState]
  );
  const handleCloseDeletePopup = useCallback(
    (params: DeleteTicketParamsT) => () => {
      deleteTicketMutation.mutate(params);
      setIsBeingDeleted(true);
    },
    [deleteTicketMutation]
  );

  return {
    isDeletingTicket: deleteTicketMutation.isLoading,
    isTicketDeleted: deleteTicketMutation.isSuccess,
    isTicketDeletedError: deleteTicketMutation.isError,
    deleteTicketErrorMessage,
    deleteTicketValidationErrorMessage,
    showPopupContent: deleteTicketMutation.isIdle || deleteTicketMutation.isLoading,
    handleCloseDeletePopup,
    handleClosePopup,
  };
};

export default useDeleteTicketPopup;
